// src/components/doctor/VitalsTab.jsx
//
// "Vitals" on the doctor's patient record: how the patient is trending, and
// every reading with who recorded it. Trend cards (BP with its ≥140/≥90 band,
// weight with BMI, pulse, SpO₂ and temperature) sit above an "All readings"
// table that doubles as the accessible alternative to the charts. Below 900px
// the table becomes labelled cards. Flags come from the server (`flags`) and
// are always shown as an arrow plus words, never colour alone.

import React, { useMemo } from 'react';
import VitalsTrendChart from './VitalsTrendChart';
import { VITAL_FLAG_LABELS } from '../../utils/consultation';
import { localDay, localTime, vitalChips } from '../../utils/consultVitals';
import { fmtDayFull, fmtDayShort } from '../../utils/activeMedicines';
import {
  newestFirst, seriesFor, bpSeries, trendAvailable, bpDelta, deltaSincePrevious, bpAboveBand,
  signed, round1, sourceShort, BP_BAND,
} from '../../utils/vitalsTrend';

const NO_TREND = 'Not enough readings to show a trend';

/** {arrow, text} when the server flagged this measurement of the reading ('' otherwise). */
function flagOf(reading, key) {
  const c = vitalChips(reading).find(x => x.key === key);
  return c && c.flagged ? { arrow: c.arrow, text: c.flagText } : null;
}

function FlagMark({ flag }) {
  if (!flag) return null;
  return <><span aria-hidden="true"> {flag.arrow}</span><span className="sr-only"> ({flag.text})</span></>;
}

function ChartCard({ title, summary, flagged, children, single, legend, testId }) {
  return (
    <div className="card prec-chart" data-testid={testId}>
      <div className="prec-chart__head">
        <h3 className="prec-chart__title">{title}</h3>
        <span className={`prec-chart__sum${flagged ? ' prec-flag' : ''}`}>{summary}</span>
      </div>
      {/* One reading is a dot, not a trend: show the value (header) and say why there is no chart. */}
      {single ? <p className="prec-chart__note">{NO_TREND}</p> : <>{children}{legend}</>}
    </div>
  );
}

function DateAxis({ points }) {
  if (!points.length) return null;
  const first = points[0];
  const last = points[points.length - 1];
  if (points.length === 1) return <div className="prec-chart__axis prec-chart__axis--one">{fmtDayShort(first.day)}</div>;
  return (
    <div className="prec-chart__axis">
      <span>{fmtDayShort(first.day)}</span>
      <span>{fmtDayShort(last.day)}</span>
    </div>
  );
}

const Swatch = ({ color, dashed, label }) => (
  <span className="prec-legend__item">
    <span className={`prec-legend__line${dashed ? ' prec-legend__line--dashed' : ''}`} style={{ borderTopColor: color }} aria-hidden="true" />
    {label}
  </span>
);

function BloodPressureCard({ readings, latest }) {
  const pts = bpSeries(readings);
  const delta = bpDelta(pts);
  const flag = latest ? flagOf(latest, 'bp') : null;
  const last = pts[pts.length - 1];
  if (!last) {
    return <ChartCard title="Blood pressure" summary="No readings" testId="chart-bp"><p className="prec-chart__note">No blood pressure recorded.</p></ChartCard>;
  }
  const high = bpAboveBand(last);
  const sum = `${last.systolic}/${last.diastolic}${flag ? ` ${flag.arrow}` : ''}${delta ? ` · ${signed(delta.systolic)}/${signed(delta.diastolic)} since ${delta.sinceLabel}` : ''}`;
  const label = `Blood pressure over ${pts.length} reading${pts.length === 1 ? '' : 's'}: `
    + pts.map(p => `${fmtDayShort(p.day)} ${p.systolic}/${p.diastolic}`).join(', ')
    + `. Latest ${last.systolic}/${last.diastolic}${high ? ', above 140/90' : ''}.`;
  const series = [
    { key: 'sys', color: 'var(--brand-accent)', points: pts.map(p => ({ t: p.t, value: p.systolic, flagged: p.systolic >= BP_BAND.systolic, label: `${fmtDayShort(p.day)}: systolic ${p.systolic}` })) },
    { key: 'dia', color: 'var(--teal)', dashed: true, points: pts.map(p => ({ t: p.t, value: p.diastolic, flagged: p.diastolic >= BP_BAND.diastolic, label: `${fmtDayShort(p.day)}: diastolic ${p.diastolic}` })) },
  ];
  return (
    <ChartCard title="Blood pressure" summary={<>{sum}{flag && <span className="sr-only"> ({flag.text})</span>}</>} flagged={high} single={!trendAvailable(pts)} testId="chart-bp"
      legend={(
        <div className="prec-legend">
          <Swatch color="var(--brand-accent)" label="Systolic" />
          <Swatch color="var(--teal)" dashed label="Diastolic" />
          <span className="prec-legend__item prec-legend__band"><span className="prec-legend__box" aria-hidden="true" />Shaded: above {BP_BAND.systolic} / {BP_BAND.diastolic} is high</span>
        </div>
      )}>
      <VitalsTrendChart series={series} band={{ from: BP_BAND.systolic }} refs={[{ value: BP_BAND.diastolic }]} ariaLabel={label} minSpan={20} />
      <DateAxis points={pts} />
    </ChartCard>
  );
}

function SimpleCard({
  title, readings, field, unit, color, latest, flagKey, testId, minSpan, extra, format = v => v,
}) {
  const pts = seriesFor(readings, field);
  const last = pts[pts.length - 1];
  if (!last) {
    return <ChartCard title={title} summary="No readings" testId={testId}><p className="prec-chart__note">Nothing recorded.</p></ChartCard>;
  }
  const d = deltaSincePrevious(pts);
  const flag = flagKey && latest && latest[field] === last.value ? flagOf(latest, flagKey) : null;
  const sum = `${format(last.value)}${unit}${flag ? ` ${flag.arrow}` : ''}${extra ? ` · ${extra}` : ''}`
    + `${d ? ` · ${signed(round1(d.change))}${unit.trim() ? ` ${unit.trim()}` : ''} since ${d.sinceLabel}` : ''}`;
  const label = `${title} over ${pts.length} reading${pts.length === 1 ? '' : 's'}: `
    + pts.map(p => `${fmtDayShort(p.day)} ${format(p.value)}${unit}`).join(', ') + '.';
  const series = [{ key: field, color, points: pts.map(p => ({ t: p.t, value: p.value, label: `${fmtDayShort(p.day)}: ${format(p.value)}${unit}` })) }];
  return (
    <ChartCard title={title} summary={<>{sum}{flag && <span className="sr-only"> ({flag.text})</span>}</>} flagged={!!flag} single={!trendAvailable(pts)} testId={testId}>
      <VitalsTrendChart series={series} ariaLabel={label} minSpan={minSpan} />
      <DateAxis points={pts} />
    </ChartCard>
  );
}

function OxygenTempCard({ readings, latest }) {
  const spo2 = seriesFor(readings, 'spo2');
  const temp = seriesFor(readings, 'temperatureC');
  const lastS = spo2[spo2.length - 1];
  const lastT = temp[temp.length - 1];
  const fS = latest && lastS && latest.spo2 === lastS.value ? flagOf(latest, 'spo2') : null;
  const fT = latest && lastT && latest.temperatureC === lastT.value ? flagOf(latest, 'temp') : null;
  if (!lastS && !lastT) {
    return <ChartCard title="SpO₂ · Temp" summary="No readings" testId="chart-spo2-temp"><p className="prec-chart__note">Nothing recorded.</p></ChartCard>;
  }
  const sum = [lastS ? `${lastS.value}%${fS ? ` ${fS.arrow}` : ''}` : null, lastT ? `${lastT.value} °C${fT ? ` ${fT.arrow}` : ''}` : null].filter(Boolean).join(' · ');
  const mk = (name, pts, unit, color) => ({
    key: name, color, points: pts.map(p => ({ t: p.t, value: p.value, label: `${fmtDayShort(p.day)}: ${p.value}${unit}` })),
  });
  const label = `SpO₂ and temperature. SpO₂: ${spo2.map(p => `${fmtDayShort(p.day)} ${p.value}%`).join(', ') || 'none'}. `
    + `Temperature: ${temp.map(p => `${fmtDayShort(p.day)} ${p.value} °C`).join(', ') || 'none'}.`;
  return (
    <ChartCard title="SpO₂ · Temp" summary={<>{sum}{(fS || fT) && <span className="sr-only"> ({[fS, fT].filter(Boolean).map(f => f.text).join(', ')})</span>}</>}
      flagged={!!(fS || fT)} single={spo2.length < 2 && temp.length < 2} testId="chart-spo2-temp"
      legend={(
        <div className="prec-legend">
          {spo2.length > 0 && <Swatch color="var(--teal)" label="SpO₂ %" />}
          {temp.length > 0 && <Swatch color="var(--brand-accent)" dashed label="Temp °C" />}
        </div>
      )}>
      {/* Two scales cannot share one axis: each line is drawn on its own, stacked. */}
      {spo2.length > 0 && <VitalsTrendChart series={[mk('spo2', spo2, '%', 'var(--teal)')]} ariaLabel={label} minSpan={4} height={70} />}
      {temp.length > 0 && <VitalsTrendChart series={[mk('temp', temp, ' °C', 'var(--brand-accent)')]} ariaLabel="Temperature trend. See the readings table for values." minSpan={1} height={70} />}
      <DateAxis points={spo2.length ? spo2 : temp} />
    </ChartCard>
  );
}

function ReadingsTable({ readings, rowFor, onOpenVisit }) {
  return (
    <div className="card prec-readings" data-testid="readings-table">
      <div className="prec-readings__head">
        <h3 className="prec-readings__title">All readings</h3>
        <span className="prec-note">Source shows who measured it: clinic, navigator or the patient at home.</span>
      </div>
      <table className="prec-table prec-table--cards">
        <thead>
          <tr>
            <th>Date</th><th>BP</th><th>Pulse</th><th>SpO₂</th><th>Temp</th><th>Weight · BMI</th><th>Flags</th><th>By</th><th><span className="sr-only">Visit</span></th>
          </tr>
        </thead>
        <tbody>
          {readings.map((r, i) => {
            const row = rowFor(r);
            const bpFlag = flagOf(r, 'bp');
            const known = new Set(['BP_HIGH', 'BP_LOW', 'SPO2_LOW', 'FEVER', 'TACHYCARDIA', 'BRADYCARDIA']);
            return (
              <tr key={`${r.measuredAt}-${i}`}>
                <td data-label="Date" id={`reading-date-${i}`}><span><strong>{fmtDayFull(localDay(r.measuredAt))}</strong> <span className="prec-vx__sub">{localTime(r.measuredAt)}</span></span></td>
                <td data-label="BP" className={bpFlag ? 'prec-flag' : undefined}>
                  <span>{r.systolic != null && r.diastolic != null ? <>{r.systolic}/{r.diastolic}<FlagMark flag={bpFlag} /></> : '—'}</span>
                </td>
                <td data-label="Pulse" className={flagOf(r, 'pulse') ? 'prec-flag' : undefined}>
                  <span>{r.pulse ?? '—'}<FlagMark flag={flagOf(r, 'pulse')} /></span>
                </td>
                <td data-label="SpO₂" className={flagOf(r, 'spo2') ? 'prec-flag' : undefined}>
                  <span>{r.spo2 != null ? `${r.spo2}%` : '—'}<FlagMark flag={flagOf(r, 'spo2')} /></span>
                </td>
                <td data-label="Temp" className={flagOf(r, 'temp') ? 'prec-flag' : undefined}>
                  <span>{r.temperatureC ?? '—'}<FlagMark flag={flagOf(r, 'temp')} /></span>
                </td>
                <td data-label="Weight · BMI">
                  {r.weightKg != null ? `${r.weightKg} kg${r.bmi != null ? ` · ${r.bmi}` : ''}` : '—'}
                </td>
                <td data-label="Flags">
                  {(r.flags || []).length === 0 ? '—' : (
                    <span className="consult-chips">
                      {r.flags.map(f => <span key={f} className="consult-chip consult-chip--flag">{known.has(f) ? VITAL_FLAG_LABELS[f] : f}</span>)}
                    </span>
                  )}
                </td>
                <td data-label="By">{sourceShort(r.source, r.recordedByName)}</td>
                <td data-label="Visit" className="prec-table__act">
                  {row && <button type="button" className="card-link" aria-describedby={`reading-date-${i}`} onClick={() => onOpenVisit(row.id)}>Visit →</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function VitalsTab({ vitals, timelineRows, onOpenVisit, onReload, onRecord }) {
  const recordBtn = onRecord && (
    <button type="button" className="btn btn-primary btn-sm" onClick={onRecord}>Record vitals</button>
  );
  const readings = useMemo(() => newestFirst(vitals.data), [vitals.data]);
  const latest = readings[0] || null;
  const rowFor = useMemo(() => reading => timelineRows.find(r => r.kind === 'visit'
    && ((reading.visitId && r.visitId === reading.visitId) || (reading.appointmentId && r.appointmentId === reading.appointmentId))) || null,
  [timelineRows]);

  if (vitals.status === 'loading' || vitals.status === 'idle') {
    return (
      <div className="prec-charts" aria-busy="true" aria-label="Loading vitals">
        {[0, 1, 2, 3].map(i => <div key={i} className="skeleton skeleton--body prec-skeleton-chart" />)}
      </div>
    );
  }
  if (vitals.status === 'error') {
    return (
      <div className="card"><div className="empty-state prec-empty" role="alert">
        <div className="empty-title">Couldn’t load vitals</div>
        <div className="empty-desc">{vitals.error} The rest of this patient’s record is unaffected.</div>
        <button type="button" className="btn btn-primary" onClick={() => onReload('vitals')}>Retry</button>
      </div></div>
    );
  }
  if (readings.length === 0) {
    return (
      <div className="card"><div className="empty-state prec-empty">
        <div className="empty-title">No vitals recorded yet</div>
        <div className="empty-desc">Readings taken at the clinic, by a navigator or by the patient will appear here with a trend.</div>
        {recordBtn}
      </div></div>
    );
  }
  return (
    <div data-testid="vitals-panel">
      {recordBtn && <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 12 }}>{recordBtn}</div>}
      <div className="prec-charts">
        <BloodPressureCard readings={readings} latest={latest} />
        <SimpleCard title="Weight" readings={readings} field="weightKg" unit=" kg" color="var(--teal)" latest={latest}
          testId="chart-weight" minSpan={4} extra={latest && latest.bmi != null && latest.weightKg != null ? `BMI ${latest.bmi}` : null} />
        <SimpleCard title="Pulse" readings={readings} field="pulse" unit=" bpm" color="var(--brand-accent)" latest={latest}
          flagKey="pulse" testId="chart-pulse" minSpan={20} />
        <OxygenTempCard readings={readings} latest={latest} />
      </div>
      <ReadingsTable readings={readings} rowFor={rowFor} onOpenVisit={onOpenVisit} />
    </div>
  );
}
